import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Login.module.css';
import { localStorageKeys } from '../../utils/constants';
import { useBranchLogin } from '../../context-api/auth/BranchLoginProvider';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import { getOnLoadRegBranchPayloadFromStorage } from '../../utils/regBranchOnLoadPayload';
import { clearBranchSessionStorage } from '../../utils/branchSessionLogout';
import { branchForgotPasswordAPI } from '../../context-api/api-services/BranchForgotPassword.service';
import { API_NETWORK_ERROR } from '../../utils/constants';

/* ---------- Eye / EyeOff SVG icons (inline, no extra deps) ---------- */
const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

/* ============================================================
   LOGIN PAGE
   ============================================================ */
export default function Login() {
  const navigate = useNavigate();
  const { onLogin, loginAPIResponse } = useBranchLogin();
  const { OnLoadRegBranchInformation } = useContext(GlobalStateDataContext);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ email: false, password: false });
  const [sessionBootLoading, setSessionBootLoading] = useState(false);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotFieldError, setForgotFieldError] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSubmitted, setForgotSubmitted] = useState(false);
  const [forgotSuccessMessage, setForgotSuccessMessage] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  /** Present only when API returns `resetToken` (non-production / testing); drives dev shortcut button. */
  const [forgotDevResetToken, setForgotDevResetToken] = useState(null);

  const loading = loginAPIResponse.loading || sessionBootLoading;

  /* Redirect if already logged in */
  useEffect(() => {
    const loginInfo = localStorage.getItem(localStorageKeys.LOGIN_INFO);
    if (loginInfo) {
      try {
        const parsed = JSON.parse(loginInfo);
        if (parsed?.token) navigate('/', { replace: true });
      } catch (_) { }
    }
  }, [navigate]);

  const validate = () => {
    const emailErr = !email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    const passErr = password.length < 1;
    setFieldErrors({ email: emailErr, password: passErr });
    if (emailErr) { setError('Please enter a valid email address.'); return false; }
    if (passErr) { setError('Password is required.'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;

    const outcome = await onLogin({
      email: email.trim(),
      password,
    });

    if (outcome.success) {
      const payload = getOnLoadRegBranchPayloadFromStorage();
      if (!payload) {
        clearBranchSessionStorage();
        setError('Session could not be prepared (missing branch or user id). Please try again.');
        return;
      }
      setSessionBootLoading(true);
      const onLoad = await OnLoadRegBranchInformation(payload);
      setSessionBootLoading(false);
      if (!onLoad?.ok) {
        clearBranchSessionStorage();
        setError(
          typeof onLoad?.message === 'string' && onLoad.message.trim()
            ? onLoad.message.trim()
            : 'Could not load branch permissions. Please try again.'
        );
        return;
      }
      navigate('/', { replace: true });
    } else {
      setError(outcome.userMessage || 'Login failed. Please try again.');
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (fieldErrors.email) setFieldErrors((p) => ({ ...p, email: false }));
    if (error) setError('');
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (fieldErrors.password) setFieldErrors((p) => ({ ...p, password: false }));
    if (error) setError('');
  };

  const openForgotPassword = () => {
    setError('');
    setForgotError('');
    setForgotSubmitted(false);
    setForgotSuccessMessage('');
    setForgotDevResetToken(null);
    setForgotFieldError(false);
    setForgotEmail(email.trim());
    setShowForgotPassword(true);
  };

  const backToLogin = () => {
    setShowForgotPassword(false);
    setForgotError('');
    setForgotSubmitted(false);
    setForgotSuccessMessage('');
    setForgotDevResetToken(null);
    setForgotFieldError(false);
    setForgotLoading(false);
  };

  const handleForgotEmailChange = (e) => {
    setForgotEmail(e.target.value);
    if (forgotFieldError) setForgotFieldError(false);
    if (forgotError) setForgotError('');
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccessMessage('');
    setForgotDevResetToken(null);
    const trimmed = forgotEmail.trim();
    const invalid = !trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (invalid) {
      setForgotFieldError(true);
      setForgotError('Please enter a valid email address.');
      return;
    }
    setForgotFieldError(false);
    setForgotLoading(true);
    const res = await branchForgotPasswordAPI({ email: trimmed });
    setForgotLoading(false);

    if (res?.hasError) {
      const raw =
        (typeof res.message === 'string' && res.message.trim()) ||
        (typeof res.error?.message === 'string' && res.error.message.trim()) ||
        '';
      setForgotError(raw || API_NETWORK_ERROR);
      return;
    }

    const body = res?.data ?? {};
    const { fetchApiTime: _fetchApiTime, ...serverBody } = body;

    if (serverBody.success === true) {
      const okMsg =
        typeof serverBody.CustomMessage === 'string' && serverBody.CustomMessage.trim()
          ? serverBody.CustomMessage.trim()
          : 'Forgot password Email sent';
      setForgotSuccessMessage(okMsg);
      const devTok =
        serverBody.resetToken != null && String(serverBody.resetToken).trim()
          ? String(serverBody.resetToken).trim()
          : null;
      setForgotDevResetToken(devTok);
      setForgotSubmitted(true);
      return;
    }

    const failMsg =
      typeof serverBody.CustomMessage === 'string' && serverBody.CustomMessage.trim()
        ? serverBody.CustomMessage.trim()
        : 'Something went wrong. Please try again.';
    setForgotError(failMsg);
  };

  return (
    <div className={styles.container}>


      {/* ========== LEFT — Form Panel ========== */}
      <div className={styles.visualPanel}>

        {/* Overlays */}
        <div className={styles.gridOverlay} />
        <div className={`${styles.ring} ${styles.ring1}`} />
        <div className={`${styles.ring} ${styles.ring2}`} />
        <div className={`${styles.ring} ${styles.ring3}`} />

        {/* Orbs */}
        <div className={`${styles.orb} ${styles.orb1}`} />
        <div className={`${styles.orb} ${styles.orb2}`} />
        <div className={`${styles.orb} ${styles.orb3}`} />
        <div className={`${styles.orb} ${styles.orb4}`} />

        {/* Sparkle dots */}
        <div className={`${styles.dot} ${styles.dot1}`} />
        <div className={`${styles.dot} ${styles.dot2}`} />
        <div className={`${styles.dot} ${styles.dot3}`} />
        <div className={`${styles.dot} ${styles.dot4}`} />
        <div className={`${styles.dot} ${styles.dot5}`} />

        {/* Main content */}
        <div className={styles.visualContent}>

          {/* Brand icon */}
          <div className={styles.brandIcon}>
            <span className={styles.brandIconInner}>
              R<span className={styles.brandIconInnerAccent}>B</span>
            </span>
          </div>

          {/* Text */}
          <p className={styles.brandHeadline}>Kitchen Operations</p>
          <h2 className={styles.brandTitle}>
            Run Smarter
            <span className={styles.brandTitleAccent}>Kitchens.</span>
          </h2>
          <p className={styles.brandSubtitle}>
            A unified platform for regional branches to manage products,
            orders, squads & marketing — all in one place.
          </p>

          {/* Stats */}
          <div className={styles.statsRow}>
            <div className={styles.statItem}>
              <span className={styles.statValue}>
                <span className={styles.statAccent}>24</span>/7
              </span>
              <span className={styles.statLabel}>Monitoring</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statValue}>Real-time</span>
              <span className={styles.statLabel}>Analytics</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statValue}>
                <span className={styles.statAccent}>100%</span>
              </span>
              <span className={styles.statLabel}>Uptime SLA</span>
            </div>
          </div>

        </div>
      </div>

      {/* ========== RIGHT — Visual Panel ========== */}
      <div className={styles.formPanel}>
        <div className={styles.formInner}>

          {/* Logo */}
          <div className={styles.logoArea}>
            <div className={styles.logoBadge}>
              R<span className={styles.logoBadgeAccent}>B</span>
            </div>
            <div className={styles.logoText}>
              <span className={styles.logoName}>Regional Branch</span>
              <span className={styles.logoTagline}>Kitchen Management</span>
            </div>
          </div>

          {/* Heading */}
          <div className={styles.headingArea}>
            {showForgotPassword ? (
              <>
                <h1 className={styles.greeting}>
                  Reset <span className={styles.greetingAccent}>password.</span>
                </h1>
                <p className={styles.subHeading}>
                  Enter the email for your account. If it is registered, you will receive reset
                  instructions.
                </p>
              </>
            ) : (
              <>
                <h1 className={styles.greeting}>
                  Welcome <span className={styles.greetingAccent}>back.</span>
                </h1>
                <p className={styles.subHeading}>
                  Sign in to your dashboard and manage your kitchens.
                </p>
              </>
            )}
          </div>

          {showForgotPassword ? (
            <form className={styles.form} onSubmit={handleForgotSubmit} noValidate>
              {forgotSubmitted && (
                <div className={styles.successAlert} role="status">
                  <span className={styles.successText}>
                    {forgotSuccessMessage ||
                      'If an account exists for that email, you will receive password reset instructions shortly. Check your inbox and spam folder.'}
                  </span>
                </div>
              )}

              {forgotError && (
                <div className={styles.errorAlert}>
                  <span className={styles.errorIcon}>&#9888;</span>
                  <span className={styles.errorText}>{forgotError}</span>
                </div>
              )}

              <div className={styles.inputWrapper}>
                <label className={styles.inputLabel} htmlFor="forgot-email">Email Address</label>
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={forgotEmail}
                  onChange={handleForgotEmailChange}
                  className={`${styles.inputField} ${forgotFieldError ? styles.inputError : ''}`}
                  disabled={forgotSubmitted || forgotLoading}
                />
              </div>

              <button type="submit" className={styles.loginBtn} disabled={forgotSubmitted || forgotLoading}>
                {forgotLoading ? (
                  <>
                    <span className={styles.spinner} />
                    <span>Sending…</span>
                  </>
                ) : (
                  'Send reset link'
                )}
              </button>

              {forgotSubmitted && forgotDevResetToken ? (
                <div className={styles.devResetTestWrap}>
                  <p className={styles.devResetTestHint}>
                    Testing only: the API returned a reset token. Production responses omit this
                    field.
                  </p>
                  <button
                    type="button"
                    className={styles.devResetTestBtn}
                    onClick={() =>
                      navigate(`/reset-password?token=${encodeURIComponent(forgotDevResetToken)}`)
                    }
                  >
                    Open reset password page (use token from response)
                  </button>
                </div>
              ) : null}

              <div className={styles.backToLoginWrap}>
                <button type="button" className={styles.footerLink} onClick={backToLogin}>
                  Back to sign in
                </button>
              </div>
            </form>
          ) : (
            <form className={styles.form} onSubmit={handleSubmit} noValidate>

              {/* Error Alert */}
              {error && (
                <div className={styles.errorAlert}>
                  <span className={styles.errorIcon}>&#9888;</span>
                  <span className={styles.errorText}>{error}</span>
                </div>
              )}

              {/* Email */}
              <div className={styles.inputWrapper}>
                <label className={styles.inputLabel} htmlFor="login-email">Email Address</label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={handleEmailChange}
                  className={`${styles.inputField} ${fieldErrors.email ? styles.inputError : ''}`}
                  disabled={loading}
                />
              </div>

              {/* Password */}
              <div className={styles.inputWrapper}>
                <label className={styles.inputLabel} htmlFor="login-password">Password</label>
                <div className={styles.inputIconWrapper}>
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={handlePasswordChange}
                    className={`${styles.inputField} ${styles.inputWithIcon} ${fieldErrors.password ? styles.inputError : ''}`}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className={styles.inputEndIcon}
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {/* Remember + Forgot */}
              <div className={styles.rowBetween}>
                <label className={styles.rememberLabel}>
                  <input
                    type="checkbox"
                    className={styles.rememberCheckbox}
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span className={styles.rememberText}>Remember me</span>
                </label>
                <button
                  type="button"
                  className={styles.forgotLink}
                  onClick={openForgotPassword}
                  disabled={loading}
                >
                  Forgot password?
                </button>
              </div>

              {/* Submit */}
              <button type="submit" className={styles.loginBtn} disabled={loading}>
                {loading ? (
                  <>
                    <span className={styles.spinner} />
                    <span>{sessionBootLoading ? 'Securing session…' : 'Signing in…'}</span>
                  </>
                ) : (
                  <>
                    <LockIcon />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>
          )}

          {/* Footer */}
          {!showForgotPassword && (
            <p className={styles.footerText} style={{ marginTop: '24px' }}>
              Don't have an account?{' '}
              <button type="button" className={styles.footerLink}>
                Contact your admin
              </button>
            </p>
          )}

        </div>
      </div>



    </div>
  );
}
